// Reads Google reviews for a Business Profile directly from the browser.
//
// The Maps JavaScript API is used rather than a `fetch` to the Places web
// service, because that web service does not send CORS headers and a browser
// call to it is blocked. Nothing here talks to our own backend.
//
// Constraints worth knowing before relying on this:
//   - Place Details returns at most FIVE reviews, however many the business has,
//     so `toReviewMeta` carries the true total for the summary line.
//   - The API key must have "Places API" enabled and be restricted by HTTP
//     referrer, because it ships in the client bundle.
//   - A Place Details request is billed.
//   - Google's terms do not permit caching this content, so it is fetched live
//     on every page load rather than stored.

import { GoogleMapsKey, GooglePlaceId } from '../config';

const SCRIPT_ID = 'asap-google-maps-places';
const CALLBACK_NAME = '__asapGooglePlacesReady';
const LOAD_TIMEOUT = 8000;

let libraryPromise = null;

// Resolves with the `places` namespace once the Maps JS API is ready.
function loadPlacesLibrary(key = GoogleMapsKey, timeout = LOAD_TIMEOUT) {
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps.places);
  }

  if (libraryPromise) {
    return libraryPromise;
  }

  libraryPromise = new Promise((resolve, reject) => {
    let settled = false;

    const finish = (settle, value) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      settle(value);
    };

    const timer = setTimeout(
      () => finish(reject, new Error('Google Maps script timed out')),
      timeout
    );

    window[CALLBACK_NAME] = () => {
      if (window.google?.maps?.places) {
        finish(resolve, window.google.maps.places);
      } else {
        finish(reject, new Error('Google Places library is unavailable'));
      }
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
      `&libraries=places&callback=${CALLBACK_NAME}`;
    script.onerror = () => finish(reject, new Error('Google Maps script failed to load'));

    document.head.appendChild(script);
  }).catch((error) => {
    // Do not cache the failure — a later mount should be able to retry.
    libraryPromise = null;
    throw error;
  });

  return libraryPromise;
}

// Place Details for one place id. Only the fields the section renders are
// requested, since the request is billed by field group.
function fetchPlace(placeId = GooglePlaceId, key = GoogleMapsKey) {
  return loadPlacesLibrary(key).then(
    (places) =>
      new Promise((resolve, reject) => {
        // PlacesService needs an element to attach attribution to; a detached
        // node is accepted, so no map is rendered on the page.
        const host = document.createElement('div');
        const service = new places.PlacesService(host);

        service.getDetails(
          { placeId, fields: ['reviews', 'rating', 'user_ratings_total'] },
          (place, status) => {
            if (status !== places.PlacesServiceStatus.OK || !place) {
              reject(new Error(`Places lookup failed: ${status}`));
              return;
            }
            resolve(place);
          }
        );
      })
  );
}

// Maps a Place Details payload onto the review card shape the section renders:
// { id, user: { name, avatar }, rating, body, addedAt }.
function toReviewCards(place) {
  const reviews = Array.isArray(place?.reviews) ? place.reviews : [];

  return reviews.map((review, index) => ({
    // Google reviews carry no stable id, so derive one from the author and time.
    id: `google-${review.time || index}-${index}`,
    user: {
      name: review.author_name || 'Google user',
      // Deliberately empty: the reviewer's photo is a third-party asset, and
      // Avatar already falls back to their initials.
      avatar: '',
    },
    rating: Number(review.rating) || 0,
    body: review.text || '',
    addedAt: review.time ? new Date(review.time * 1000).toISOString() : null,
  }));
}

// The figures for the summary line, in the shape the section expects.
//
// Five returned reviews cannot stand in for the real average and total, so
// neither figure is ever derived from the cards. Each is null when Place Details
// did not report it, and the caller renders nothing for a null. In particular
// `totalReviews` does NOT fall back to `reviews.length`: a count of five beside
// a five-star average reads as "based on 5 Google reviews" on a business with a
// thousand, which is the false statement this whole path exists to avoid.
//
// The key names match the backend `review_meta` payload (`rating`,
// `totalReviews`, `capturedAt`) so the section consumes either source with one
// shape.
export function toReviewMeta(place) {
  const rating = Number(place?.rating);
  const total = Number(place?.user_ratings_total);

  return {
    // Guarded rather than coerced: a rating outside (0, 5] cannot have come from
    // Google, and Number(undefined) is NaN, which is not a figure.
    rating: Number.isFinite(rating) && rating > 0 && rating <= 5 ? rating : null,
    totalReviews: Number.isFinite(total) && total > 0 ? Math.round(total) : null,
    // When Google reported it. Makes a stale figure visibly old rather than
    // silently current.
    capturedAt: new Date().toISOString(),
  };
}

// Convenience wrapper: resolves with { reviews, meta } and never rejects.
export async function loadGoogleReviews(placeId = GooglePlaceId, key = GoogleMapsKey) {
  try {
    const place = await fetchPlace(placeId, key);
    const reviews = toReviewCards(place);
    return { reviews, meta: toReviewMeta(place) };
  } catch (error) {
    return { reviews: [], meta: null, error };
  }
}